import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
`;

const pacmanMove = keyframes`
  0% { transform: translateX(-50px) rotate(0deg); }
  100% { transform: translateX(350px) rotate(0deg); }
`;

const pacmanMouth = keyframes`
  0% { 
    clip-path: polygon(50% 50%, 50% 0%, 100% 0%, 100% 100%, 50% 100%);
  }
  50% { 
    clip-path: polygon(50% 50%, 50% 0%, 100% 0%, 100% 50%, 100% 100%, 50% 100%);
  }
  100% { 
    clip-path: polygon(50% 50%, 50% 0%, 100% 0%, 100% 100%, 50% 100%);
  }
`;

const dotBlink = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(0.8); }
`;

const LoadingContainer = styled.div`
  width: 100vw;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.3) 0%, transparent 50%);
`;

const LoadingSpinner = styled.div`
  width: 80px;
  height: 80px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid #fff;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 30px;
`;

const LoadingText = styled(motion.h1)`
  color: white;
  font-size: 2.5rem;
  font-weight: 300;
  text-align: center;
  margin-bottom: 20px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 1024px) {
    font-size: 2rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 10px;
  }
`;

const PacmanContainer = styled.div`
  width: 400px;
  height: 100px;
  position: relative;
  margin: 40px 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  
  @media (max-width: 1024px) {
    width: 350px;
    height: 90px;
    margin: 30px 0;
  }
  
  @media (max-width: 768px) {
    width: 300px;
    height: 80px;
    margin: 25px 0;
  }
  
  @media (max-width: 480px) {
    width: 250px;
    height: 70px;
    margin: 20px 0;
  }
`;

const Pacman = styled.div`
  width: 50px;
  height: 50px;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  animation: ${pacmanMove} 2s linear infinite;
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    width: 50px;
    height: 50px;
    background: #FFD700;
    border-radius: 50%;
    clip-path: polygon(50% 50%, 50% 0%, 100% 0%, 100% 100%, 50% 100%);
    animation: ${pacmanMouth} 0.3s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-30%, -50%);
    width: 8px;
    height: 8px;
    background: #000;
    border-radius: 50%;
  }
`;

const Dot = styled.div`
  width: 12px;
  height: 12px;
  background: #FFD700;
  border-radius: 50%;
  position: absolute;
  left: ${props => props.$position}px;
  animation: ${dotBlink} 1s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
`;

const LoadingSubtext = styled(motion.p)`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.2rem;
  text-align: center;
  max-width: 400px;
  line-height: 1.6;
  
  @media (max-width: 1024px) {
    font-size: 1.1rem;
    max-width: 350px;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    max-width: 300px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    max-width: 250px;
  }
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;
`;

const SimpleDot = styled(motion.div)`
  width: 12px;
  height: 12px;
  background: white;
  border-radius: 50%;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const LoadingScreen = () => {
  // Generate dots for pacman to "eat"
  const dots = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    position: 80 + i * 40,
    delay: i * 0.1
  }));

  return (
    <LoadingContainer>
      <BackgroundPattern />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <LoadingText
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          Storytelling Game
        </LoadingText>
        
        <PacmanContainer>
          <Pacman />
          {dots.map(dot => (
            <Dot key={dot.id} $position={dot.position} $delay={dot.delay} />
          ))}
        </PacmanContainer>
        
        <LoadingSubtext
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          Preparing your interactive educational experience...
        </LoadingSubtext>
        <LoadingDots>
          <SimpleDot
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
          />
          <SimpleDot
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
          />
          <SimpleDot
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
          />
        </LoadingDots>
      </motion.div>
    </LoadingContainer>
  );
};

export default LoadingScreen;


